import React, { forwardRef, useRef, useState } from 'react';
import classNames from 'classnames';
import ErrorMessage from './ErrorMessage';

interface DateInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
	label?: string;
	error?: string;
	required?: boolean;
	onClear?: () => void;
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
	({ label, error, required, className, id, onClear, onChange, defaultValue, ...props }, ref) => {
		const inputId = id || label?.replace(/\s+/g, '-').toLowerCase();
		const internalRef = useRef<HTMLInputElement | null>(null);
		const [internalValue, setInternalValue] = useState(defaultValue ?? '');

		const mergeRefs = (node: HTMLInputElement | null) => {
			internalRef.current = node;
			if (typeof ref === 'function') {
				ref(node);
			} else if (ref) {
				(ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
			}
		};

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			setInternalValue(e.target.value);
			onChange?.(e);
		};

		const handleClear = () => {
			setInternalValue('');
			// Clear the native input DOM value for uncontrolled (register) usage
			if (internalRef.current) {
				internalRef.current.value = '';
			}
			onClear?.();
		};

		return (
			<div className="flex flex-col gap-1 w-full">
				{label && (
					<label htmlFor={inputId} className="text-sm text-gray-800">
						{label} {required && <span className="text-[var(--colour-brand-primary)]">*</span>}
					</label>
				)}
				<div className="flex relative items-center custom-input">
					<input
						id={inputId}
						ref={mergeRefs}
						type="date"
						defaultValue={defaultValue}
						onChange={handleChange}
						className={classNames(
							'px-3 py-3 border rounded-md outline-none transition-all w-full cursor-pointer',
							'placeholder:text-gray-400 text-sm',
							'[&::-webkit-calendar-picker-indicator]:absolute',
							'[&::-webkit-calendar-picker-indicator]:inset-0',
							'[&::-webkit-calendar-picker-indicator]:w-full',
							'[&::-webkit-calendar-picker-indicator]:h-full',
							'[&::-webkit-calendar-picker-indicator]:opacity-0',
							'[&::-webkit-calendar-picker-indicator]:cursor-pointer',
							// eslint-disable-next-line no-nested-ternary
							props.disabled
								? 'border-gray-300 bg-gray-100 cursor-not-allowed text-gray-500 placeholder:text-gray-300'
								: error
									? 'border-red-400 bg-red-50 text-red-900 focus:border-red-400 focus:ring-1 focus:ring-[#ef4444]'
									: 'border-gray-300 hover:border-gray-400 focus:border-[var(--colour-brand-primary)] focus:ring-1 focus:ring-[var(--colour-brand-primary)]',
							props.readOnly && 'bg-gray-50 text-gray-600',
							className,
						)}
						{...props}
					/>
					{onClear && !props.readOnly && !props.disabled && internalValue && (
						<button
							type="button"
							className="absolute right-10 text-gray-400 hover:text-gray-600"
							onClick={handleClear}
						>
							<span className="material-symbols-outlined text-base">close</span>
						</button>
					)}
				</div>
				<ErrorMessage error={error} />
			</div>
		);
	},
);
